import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { Loader, LoaderModeType } from 'components/Loader';
import { SectionContainer } from 'components/DetailsView/Section';

import {
    useGetSupplyOrderProductsToAddQuery,
    useGetSupplyOrderSupplierProductsStockVariationQuery,
} from 'data/__generated__';

import { ProductsInfoStockVariation } from '../../shared/stockVariation/ProductsInfoStockVariation';
import { SupplyOrderDetails } from '../supplyOrderFormValues';

/**
 * Prévisions et variations de stock : la requête LOURDE de la fiche.
 *
 * La section est `lazy` — sa requête ne part qu'à la première ouverture. Ouvrir un achat pour corriger
 * une quantité ne doit pas payer le calcul de prévisions de toutes ses références.
 */
export const ForecastsSection = ({ supplyOrder }: { supplyOrder: SupplyOrderDetails }) => (
    <SectionContainer title="Prévisions et variations de stock" lazy>
        <ForecastsContent supplyOrder={supplyOrder} />
    </SectionContainer>
);

type ForecastProduct = {
    _id: string;
    name: string;
    brand: string;
    volume: string;
    isToBeArchived: boolean;
    stock: number;
    isOrdered: boolean;
    // Les besoins calculés par le serveur, à l'origine des quantités proposées. `null` sur une référence
    // non commandée : ce sont des champs de la LIGNE d'achat, le catalogue ne les porte pas.
    quantityGeneratedForMenus: number | null;
    quantityGenerated: number | null;
};

const ForecastsContent = ({ supplyOrder }: { supplyOrder: SupplyOrderDetails }) => {
    const { data, loading, error } = useGetSupplyOrderSupplierProductsStockVariationQuery({
        variables: { supplyOrderId: supplyOrder._id },
    });

    // Les variations sont calculées pour TOUT le catalogue du fournisseur, pas seulement pour ce qui est
    // commandé : il faut donc aller chercher l'identité des références non commandées. Même requête que
    // la section « Produits du fournisseur non commandés » — Apollo la sert depuis son cache si elle a
    // déjà été ouverte.
    const { data: productsToAddData } = useGetSupplyOrderProductsToAddQuery({
        variables: { supplyOrderId: supplyOrder._id },
    });

    const productsById = useMemo(() => {
        const products = new Map<string, ForecastProduct>();

        supplyOrder.products.forEach(
            ({ _id, name, brand, volume, isToBeArchived, stock, quantityGenerated, quantityGeneratedForMenus }) => {
                products.set(_id, {
                    _id,
                    name,
                    brand,
                    volume,
                    isToBeArchived,
                    stock,
                    isOrdered: true,
                    quantityGeneratedForMenus,
                    quantityGenerated,
                });
            },
        );

        (productsToAddData?.supplyOrderProductsToAdd ?? []).forEach(
            ({ _id, name, brand, volume, isToBeArchived, stock }) => {
                products.set(_id, {
                    _id,
                    name,
                    brand,
                    volume,
                    isToBeArchived,
                    stock,
                    isOrdered: false,
                    quantityGeneratedForMenus: null,
                    quantityGenerated: null,
                });
            },
        );

        return products;
    }, [supplyOrder.products, productsToAddData]);

    const stockVariations = data?.supplyOrderSupplierProductsStockVariation.supplyOrderStockVariations ?? [];

    // Une ligne par référence du fournisseur, pas par variation enregistrée : les variations plates
    // (aucun mouvement sur la fenêtre, seuil de réserve à 0) ne sont pas stockées — cf.
    // `filterOutZeroStockVariations` côté API — et la référence disparaissait alors du tableau.
    // Les produits commandés d'abord, ce sont eux qu'on vient vérifier ; le reste par ordre alphabétique.
    const rows = useMemo(() => {
        const variationByProductId = new Map(stockVariations.map((variation) => [variation.productId, variation]));

        const catalogueRows = [...productsById.values()].map((product) => ({
            productId: product._id,
            product,
            stockVariation: variationByProductId.get(product._id) ?? null,
        }));

        // Une variation sans référence au catalogue : le produit a quitté le fournisseur depuis le calcul.
        const orphanRows = stockVariations
            .filter(({ productId }) => !productsById.has(productId))
            .map((stockVariation) => ({ productId: stockVariation.productId, product: null, stockVariation }));

        return [...catalogueRows, ...orphanRows].sort((a, b) => {
            if (!!a.product?.isOrdered !== !!b.product?.isOrdered) {
                return a.product?.isOrdered ? -1 : 1;
            }
            return (a.product?.name ?? '').localeCompare(b.product?.name ?? '');
        });
    }, [stockVariations, productsById]);

    if (loading) {
        return (
            <div className="flex h-40 w-full items-center justify-center">
                <Loader mode={LoaderModeType.Spin} />
            </div>
        );
    }

    if (error) {
        return <p className="text-sm text-destructive">Erreur lors du chargement des variations de stock.</p>;
    }

    if (!rows.length) {
        return <p className="text-sm text-muted-foreground">Aucune variation de stock calculée pour cet achat.</p>;
    }

    return (
        <div className="flex flex-col gap-2 overflow-x-auto">
            <p className="text-xs text-muted-foreground">
                {rows.length} référence(s) du fournisseur — les prévisions portent sur tout le catalogue, pas seulement
                sur ce qui est commandé. Une référence sans variation n'a aucun mouvement prévu sur la fenêtre.
            </p>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Produit</TableHead>
                        <TableHead>Stock</TableHead>
                        <TableHead>Qté menus</TableHead>
                        <TableHead>Qté prédictions</TableHead>
                        <TableHead>Variation de stock</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map(({ productId, stockVariation, product }, index) => (
                        <TableRow key={productId}>
                            <TableCell className="whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                    <Link className="hover:underline" to={`/product/${productId}`}>
                                        {product
                                            ? `${product.name} - ${product.brand} - ${product.volume}`
                                            : `Référence ${productId}`}
                                    </Link>
                                    {product?.isOrdered ? <Badge variant="secondary">Commandé</Badge> : null}
                                    {product?.isToBeArchived ? <Badge variant="outline">À sortir</Badge> : null}
                                </div>
                            </TableCell>
                            <TableCell>{product?.stock ?? '—'}</TableCell>
                            <TableCell>{product?.quantityGeneratedForMenus ?? '—'}</TableCell>
                            <TableCell>{product?.quantityGenerated ?? '—'}</TableCell>
                            <TableCell>
                                {stockVariation ? (
                                    <ProductsInfoStockVariation
                                        isComputed={stockVariation.isComputed}
                                        orderingTimes={supplyOrder.supplier.orderingTimes}
                                        productInfo={
                                            product ?? {
                                                name: `Référence ${productId}`,
                                                brand: '',
                                                volume: '',
                                                isToBeArchived: false,
                                            }
                                        }
                                        productStockVariation={stockVariation}
                                        stock={product?.stock ?? 0}
                                        startDate={stockVariation.orderDate}
                                        // Un ReactTooltip PAR JOUR et par ligne : au-delà de quelques lignes,
                                        // l'onglet se fige. Les trois premières suffisent à donner la légende.
                                        shouldDisplayTooltip={index < 3}
                                    />
                                ) : (
                                    <span className="text-muted-foreground">Aucun mouvement prévu</span>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};
